// El comprobante de transferencia lo sube el COMERCIO y lo abre el OPERADOR. El tipo que
// trae la respuesta (`blob.type`) nace del archivo subido —el front mismo le manda
// `proofMime: file.type` al backend (store/cobro.js)— así que es un dato del atacante: con
// `text/html` la blob: URL se abre en el ORIGEN DE LA APP (una blob: hereda el origen del
// documento que la crea) y el script lee el accessToken del localStorage.
//
// Por eso el tipo no se cree: se deduce de los bytes. De paso arregla el caso inverso, un
// PDF legítimo que el proxy entrega como octet-stream (así lo hace el S3, ver
// DocumentViewerModal): con el tipo correcto el visor lo muestra en vez de descargarlo.

/** Tipo de los comprobantes que no se pueden reconocer: el navegador los descarga. */
export const PROOF_UNKNOWN_TYPE = 'application/octet-stream';

// Bytes que alcanzan para todas las firmas de abajo (WEBP necesita 12).
const HEAD_BYTES = 16;

/**
 * Deduce el tipo de un comprobante por su firma binaria. Solo los formatos que el comercio
 * puede adjuntar (PaymentTransfer.jsx) más WEBP; cualquier otra cosa —HTML, SVG, texto—
 * cae en octet-stream a propósito.
 *
 * @param {Uint8Array|number[]} bytes primeros bytes del archivo
 * @returns {string} MIME deducido, o `PROOF_UNKNOWN_TYPE`
 */
export function sniffProofMime(bytes) {
  const b = bytes || [];
  const at = (i) => b[i];
  // %PDF
  if (at(0) === 0x25 && at(1) === 0x50 && at(2) === 0x44 && at(3) === 0x46) return 'application/pdf';
  // \x89PNG
  if (at(0) === 0x89 && at(1) === 0x50 && at(2) === 0x4e && at(3) === 0x47) return 'image/png';
  // JPEG (SOI + marcador)
  if (at(0) === 0xff && at(1) === 0xd8 && at(2) === 0xff) return 'image/jpeg';
  // RIFF....WEBP
  if (
    at(0) === 0x52 && at(1) === 0x49 && at(2) === 0x46 && at(3) === 0x46 &&
    at(8) === 0x57 && at(9) === 0x45 && at(10) === 0x42 && at(11) === 0x50
  ) {
    return 'image/webp';
  }
  return PROOF_UNKNOWN_TYPE;
}

/**
 * Lee la cabecera del blob. Con FileReader y no con `blob.arrayBuffer()`: el jsdom de
 * react-scripts test no implementa `arrayBuffer`, y así el helper es testeable.
 *
 * @param {Blob} blob
 * @returns {Promise<Uint8Array>}
 */
function readHead(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el comprobante.'));
    reader.readAsArrayBuffer(blob.slice(0, HEAD_BYTES));
  });
}

/**
 * Devuelve el mismo contenido con el tipo deducido de los bytes. El tipo que venía en la
 * respuesta se descarta siempre.
 *
 * @param {Blob} blob comprobante tal como lo devolvió la API
 * @returns {Promise<Blob>} blob con el tipo deducido (`PROOF_UNKNOWN_TYPE` si no se reconoce)
 */
export async function retypeProofBlob(blob) {
  const type = sniffProofMime(await readHead(blob));
  return new Blob([blob], { type });
}
